import React, { Dispatch, SetStateAction, useState } from "react";
import styled from "styled-components";
import Lottie from "lottie-react";

import lottie_loading from "../../assets/lottie_loading.json";
import Cancel from "../../assets/cancel.png";
import { Common } from "../../styles/Common";
import { useSearchPartner } from "../../hooks/userPartnerApi";

interface Props {
    setPartnerSearchOpen: Dispatch<SetStateAction<boolean>>;
    onSubmitConfirmCode: () => void;
    partnerSearchOpen: boolean;
    setPartnerCode: (code: string) => void;
}

const PartnerSearchModal: React.FC<Props> = ({
    onSubmitConfirmCode,
    partnerSearchOpen,
    setPartnerSearchOpen,
    setPartnerCode,
}) => {
    const [searchPartnerContent, setSearchPartnerContent] = useState("");
    const [selectPartner, setSelectPartner] = useState("");
    const [clickedSearch, setClickedSearch] = useState(false);
    const { data, refetch, isFetching } = useSearchPartner(searchPartnerContent);

    const onSearchHandler = () => {
        refetch();
        setClickedSearch(true);
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") onSearchHandler();
    };

    const onCloseView = () => {
        setClickedSearch(false);
        setPartnerSearchOpen(false);
        setPartnerCode("");
        setSelectPartner("");
        setSearchPartnerContent("");
    };

    const onSubmit = () => {
        setClickedSearch(false);
        setPartnerSearchOpen(false);
        setSearchPartnerContent("");
        setSelectPartner("");
        onSubmitConfirmCode();
    };

    if (!partnerSearchOpen) return null;

    return (
        <>
            <Overlay onClick={onCloseView} />
            <SheetWrapper onClick={(e) => e.stopPropagation()}>
                <HeaderWrapper>
                    <Title>추천인 조회하기</Title>
                    <CloseButton src={Cancel} alt="닫기" onClick={onCloseView} />
                </HeaderWrapper>

                <ScrollContent>
                    <SearchInputWrapper>
                        <StyledInput
                            placeholder="이름, 추천인 코드를 입력해주세요."
                            value={searchPartnerContent}
                            onChange={(e) => setSearchPartnerContent(e.target.value)}
                            onKeyDown={handleKeyPress}
                        />
                        <SearchButton
                            $active={searchPartnerContent.trim().length > 0}
                            onClick={onSearchHandler}
                            disabled={!searchPartnerContent.trim().length}
                        >
                            검색
                        </SearchButton>
                    </SearchInputWrapper>

                    {isFetching ? (
                        <LottieBox>
                            <Lottie className="lottie" animationData={lottie_loading} />
                        </LottieBox>
                    ) : data ? (
                        <>
                            {data.list?.length ? (
                                <ResultList>
                                    {data.list.map((item: any) => (
                                        <div
                                            className="result-item"
                                            key={item.code}
                                            onClick={() => {
                                                setSelectPartner(item.code);
                                                setPartnerCode(item.code);
                                            }}
                                        >
                                            <p>{item.name}</p>
                                            <span>{item.code}</span>
                                        </div>
                                    ))}
                                </ResultList>
                            ) : (
                                <ResultList>
                                    <p>검색결과가 없습니다.</p>
                                </ResultList>
                            )}
                        </>
                    ) : null}
                </ScrollContent>

                <Footer>
                    <ConfirmButton $active={selectPartner.length > 1} onClick={onSubmit}>
                        확인
                    </ConfirmButton>
                </Footer>
            </SheetWrapper>
        </>
    );
};

export default PartnerSearchModal;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 3000;
`;

const SheetWrapper = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background: white;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    min-height: 60vh;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    z-index: 3001;
    animation: slideUp 0.3s ease-out;

    @keyframes slideUp {
        from {
            transform: translateY(100%);
        }
        to {
            transform: translateY(0%);
        }
    }
`;

const HeaderWrapper = styled.div`
    padding: 20px 16px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Title = styled.p`
    font-size: 24px;
    font-weight: 700;
    line-height: 140%;
`;

const CloseButton = styled.img`
    width: 16px;
    height: 16px;
    cursor: pointer;
`;

const ScrollContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0 16px;
`;

const SearchInputWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
`;

const StyledInput = styled.input`
    flex: 1;
    height: 48px;
    padding: 0 14px;
    border-radius: 12px;
    background: #f5f5f5;
    border: none;
    font-size: 14px;
    outline: none;

    &::placeholder {
        color: #b0b0b0;
    }
`;

const SearchButton = styled.button<{ $active: boolean }>`
    height: 48px;
    padding: 0 16px;
    border-radius: 12px;
    background: ${({ $active }) => ($active ? Common.colors.aucMarkerColor : "#d3d3d3")};
    color: white;
    font-size: 14px;
    font-weight: 500;
    border: none;
    cursor: ${({ $active }) => ($active ? "pointer" : "default")};
`;

const ResultList = styled.div`
    margin-top: 20px;

    .result-item {
        display: flex;
        justify-content: space-between;
        padding: 12px 0;
        border-bottom: 1px solid #eee;
        cursor: pointer;

        &:hover {
            background: #f2f7ff;
        }

        p {
            font-size: 14px;
            color: #333;
        }

        span {
            font-size: 14px;
            color: #888;
        }
    }
`;

const Footer = styled.div`
    padding: 16px;
`;

const ConfirmButton = styled.button<{ $active: boolean }>`
    width: 100%;
    height: 48px;
    border-radius: 12px;
    background: ${({ $active }) => ($active ? "#2275EE" : "#D3D3D3")};
    color: white;
    font-size: 16px;
    font-weight: 600;
    border: none;
    cursor: ${({ $active }) => ($active ? "pointer" : "default")};
`;

const LottieBox = styled.div`
    width: 100%;
    height: 180px;
    display: flex;
    justify-content: center;
    align-items: center;

    .lottie {
        width: 72px;
    }
`;
